import React from "react";
import { KeenIcon } from "@/components";
import {
  useDeleteGeoJsonFileMutation,
  useDeleteManualAnnotationMutation,
} from "@/services/RoutingManagementService";
import { useSnackbar } from "notistack";

interface DeleteRouteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDeleteSuccess: () => void;
  route: {
    id: number;
    filename: string;
    type?: "uploaded" | "manual";
  };
}

const DeleteRouteModal: React.FC<DeleteRouteModalProps> = ({
  isOpen,
  onClose,
  onDeleteSuccess,
  route,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [deleteGeoJsonFile] = useDeleteGeoJsonFileMutation();
  const [deleteManualAnnotation] = useDeleteManualAnnotationMutation();
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      if (route.type === "manual") {
        await deleteManualAnnotation(route.id).unwrap();
      } else {
        await deleteGeoJsonFile(route.id).unwrap();
      }
      enqueueSnackbar("Route deleted successfully", { variant: "success" });
      onDeleteSuccess();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string }; message?: string };
      enqueueSnackbar(
        `Failed to delete route: ${error?.data?.message || error?.message || "Unknown error"}`,
        { variant: "error" },
      );
    } finally {
      setIsDeleting(false);
      try {
        onClose();
      } catch (error) {
        console.error("Error in onClose callback:", error);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-gray-100 rounded-lg shadow-xl w-full max-w-md m-4">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">
            Confirm Deletion
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label="Close modal"
            disabled={isDeleting}
          >
            <KeenIcon icon="cross" className="text-lg" />
          </button>
        </div>
        <div className="p-6">
          <p className="text-gray-600 mb-4">
            Are you sure you want to delete <strong>{route.filename}</strong>?
            This action cannot be undone.
          </p>
          {isDeleting && (
            <div className="mt-4 text-gray-600 text-sm flex items-center gap-2">
              <KeenIcon icon="spinner" className="animate-spin text-lg" />
              <span>Deleting...</span>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="btn btn-sm btn-light"
            aria-label="Cancel deletion"
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmDelete}
            className="btn btn-sm btn-danger"
            aria-label="Confirm deletion"
            disabled={isDeleting}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteRouteModal;
